import React from "react";
import { Dialog, Box, IconButton, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faTimes } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { VeBox, VeTypography } from "../../mui";
import { VeButton } from "..";
import "./modal.scss";

interface InputModalProps {
  children?: React.ReactNode;
  title?: string;
  goBack?: boolean;
  desc?: string | JSX.Element;
  icon?: IconProp;
  iconSvg?: React.ReactNode | JSX.Element;
  styleColor?: string;
  handleGoBack?: () => void;
  outlinedButtonTitle?: string;
  disableContainedButton?: boolean;
  containedButtonTitle?: string;
  containedOnClick?: () => void;
  outlinedOnClick?: () => void;
  width?: string;
  open: boolean; // Determine if modal is open or closed
  onClose?: () => void; // Function to handle modal close
  loading?: boolean;
  optionalIcon?: any;
  containedButtonBelowDetails?: React.ReactNode;
  descBlock?: boolean;
  lightbackDrop?: boolean;
  minWidth?: string;
  imageIcon?: any;
}

const useStyles = makeStyles({
  hover: {
    "&:hover": { background: "#f3f3f3", borderRadius: "100%" },
    // marginTop: 15,
  },
  modal: {
    "& .MuiPaper-rounded": {
      borderRadius: "8px !important",
    },
  },
});

export function SuccessModal({
  title,
  goBack,
  desc,
  icon,
  styleColor,
  handleGoBack,
  loading,
  disableContainedButton,
  containedButtonTitle,
  containedOnClick,
  outlinedButtonTitle,
  outlinedOnClick,
  iconSvg,
  width = "436px",
  minWidth = "none",
  open,
  onClose,
  containedButtonBelowDetails,
  optionalIcon,
  descBlock = false,
  lightbackDrop = false,
  imageIcon,
}: InputModalProps) {
  const classes = useStyles();

  const isLargeScreen = useMediaQuery("(min-width:1534px)");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.modal}
      aria-labelledby="responsive-dialog-title"
      disableScrollLock
      hideBackdrop={false}
      sx={{ overflow: "scroll !important" }}
      PaperProps={{
        sx: {
          boxShadow:
            "0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1) !important",
          overflow: "none",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: lightbackDrop
              ? "rgba(232, 232, 232, 0.6)"
              : "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      <Box
        maxWidth={width}
        minWidth={minWidth}
        sx={{ padding: "16px 16px 24px 16px" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {goBack && (
              <Box onClick={handleGoBack}>
                <FontAwesomeIcon
                  width="22px"
                  height="22px"
                  icon={faChevronLeft}
                />
              </Box>
            )}
          </Box>

          <Box
            display="flex"
            justifyContent="center"
            sx={{ alignItems: "center" }}
            // pb="1.5rem"
          ></Box>
          {onClose ? (
            <Box
              onClick={onClose}
              sx={{
                cursor: "pointer",
                // marginLeft: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CloseIcon />
            </Box>
          ) : (
            <div />
          )}
        </Box>
        <Box>
          <Box>
            {icon ? (
              <Box
                display="flex"
                justifyContent="center"
                // pb="1.5rem"
                // pt="2rem"
                sx={{ alignItems: "center" }}
              >
                {" "}
                <Box sx={{ textAlign: "center" }}>
                  <FontAwesomeIcon
                    icon={icon}
                    style={{
                      color: styleColor,
                      width: "3rem",
                      height: "3rem",
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                // pb="1.5rem"
                paddingTop="1rem"
                sx={{ alignItems: "center" }}
              >
                {" "}
                <Box sx={{ textAlign: "center" }}>
                  {imageIcon ? imageIcon : iconSvg}
                </Box>
              </Box>
            )}

            {title && (
              <Box
                pt="1.5rem"
                pb="1rem"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <VeTypography variant="subtitle1Main" align="center" p={0}>
                  {title}
                </VeTypography>
              </Box>
            )}

            {desc && descBlock ? (
              <Box maxWidth="436px" paddingBottom="1.5rem">
                {desc}
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                paddingBottom="1.5rem"
                sx={{ alignItems: "center" }}
                maxWidth="436px"
              >
                <VeTypography variant="body2Disabled" align="center" p={0}>
                  {desc}
                </VeTypography>
              </Box>
            )}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <>
                  {outlinedButtonTitle && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <VeButton
                        buttonVariant="outlined"
                        color="primary"
                        size="medium"
                        buttonClick={outlinedOnClick}
                        buttonMinWidth="96px"
                      >
                        {outlinedButtonTitle}
                      </VeButton>
                    </Box>
                  )}
                  {containedButtonTitle && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <VeButton
                        buttonVariant="contained"
                        color="primary"
                        size="medium"
                        buttonClick={containedOnClick}
                        disabled={disableContainedButton}
                        loading={loading}
                        buttonMinWidth="96px"
                      >
                        {containedButtonTitle}
                      </VeButton>
                    </Box>
                  )}
                </>
              </Box>
            </Box>
            {containedButtonBelowDetails && (
              <VeBox marginTop="12px" display="flex" justifyContent="center">
                {containedButtonBelowDetails}
              </VeBox>
            )}
          </Box>{" "}
        </Box>
      </Box>
    </Dialog>
  );
}
const CloseIcon = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.295 2.115C13.6844 1.72564 13.6844 1.09436 13.295 0.705C12.9056 0.315639 12.2744 0.315639 11.885 0.705L7 5.59L2.115 0.705C1.72564 0.315639 1.09436 0.315639 0.705 0.705C0.315639 1.09436 0.315639 1.72564 0.705 2.115L5.59 7L0.705 11.885C0.315639 12.2744 0.315639 12.9056 0.705 13.295C1.09436 13.6844 1.72564 13.6844 2.115 13.295L7 8.41L11.885 13.295C12.2744 13.6844 12.9056 13.6844 13.295 13.295C13.6844 12.9056 13.6844 12.2744 13.295 11.885L8.41 7L13.295 2.115Z"
        fill="#5F6368"
        width="14"
        height="14"
      />
    </svg>
  );
};
